/**
 * Per-page titles and descriptions.
 *
 * They live in ONE object rather than next to each route so that "no two pages
 * share a title" is a property something can check. meta.test.ts checks it.
 *
 * Shape of a title: `<Page> | KEV`, except the landing page, which carries the
 * brand line instead. Search results truncate around 60 characters, so the
 * distinguishing word goes first and "KEV" goes last where losing it costs
 * nothing.
 *
 * `robots: "noindex"` marks a page that must never be indexed even on an
 * indexable build — signed-in surfaces, one-shot callbacks, and pages whose
 * content is a function of who is looking. That is a per-page property, not a
 * per-build one, so it sits here and not in config/site.ts.
 */
export interface PageMetaEntry {
  title: string;
  description: string;
  /** Defaults to indexable-if-the-build-is. Set to opt a single page out. */
  robots?: "noindex";
}

export const PAGE_META = {
  landing: {
    title: "KEV | Your Sharpest Mate",
    description:
      "Australian sports analytics that shows its working. 18+.",
  },
  about: {
    title: "About | KEV",
    description:
      "Why KEV exists, what its models do with seasons of sports data, and the person who built it. 18+.",
  },
  team: {
    title: "Meet the team | KEV",
    description:
      "Kev prices probabilities from historical data. Franco answers roster questions. What each KEV model is for, and where each one stops. 18+.",
  },
  pricing: {
    title: "Pricing | KEV",
    description:
      "Premium or Pro: every KEV model on either plan, billed monthly in AUD with GST included, and a monthly credit allowance on both. 18+.",
  },
  board: {
    title: "The Board | KEV",
    /* "free with a KEV account" twice over became one claim too many once "free"
       stopped naming the tier (Ned, 2026-08-29) — see TIER_LABEL in plans.ts.
       The board still costs nothing; it just no longer says so in a way that
       also implies the reader has not bought anything. */
    description:
      "What KEV's models are calling today, and how the last slate landed. Included with every KEV account.",
    robots: "noindex",
  },
  login: {
    title: "Sign in | KEV",
    description: "Sign in to your KEV account.",
    robots: "noindex",
  },
  signup: {
    title: "Create an account | KEV",
    description: "Create a KEV account.",
    robots: "noindex",
  },
  authCallback: {
    title: "Signing you in | KEV",
    description: "Completing your sign-in and returning you to KEV.",
    robots: "noindex",
  },
  welcome: {
    title: "Finish setting up | KEV",
    description: "A few details before KEV can get started, and what you're agreeing to.",
    robots: "noindex",
  },
  /* The re-consent page. Behind the auth guard and noindex like every other
     signed-in surface — a crawled copy would show a logged-out shell under a
     title about someone's account. */
  termsUpdated: {
    title: "Our terms have changed | KEV",
    description: "Accept the current Terms of Service and Privacy Policy to carry on.",
    robots: "noindex",
  },
  directory: {
    title: "Your models | KEV",
    description: "Every KEV model you can reach, and the state of the ones you can't yet.",
    robots: "noindex",
  },
  /* The landing spot — every upcoming fixture, every sport, no account
     needed — see routes/Home.tsx, mounted at `/` (App.tsx). REBUILT
     2026-09-07 against kev-docs/kev-landing-and-seo-plan.md's three-tier
     revision: this page carries Kev's read with no numbers and a value
     indicator, never a probability or a price — "the existence of an edge is
     public, its size is not" — so the description below must not claim one
     either. INDEXABLE, and now honestly so: it is the one page in this file
     built to be read with no account at all, which is why `config/routes.ts`
     lists it at priority 1.0. Prerendering is the remaining SEO step (see
     that doc's SEO section — gated on the apex cutover, not done here). */
  home: {
    title: "What Kev's calling | KEV",
    description:
      "Every upcoming match Kev covers, his read, and whether he's found value — sign in free for the numbers.",
  },
  insights: {
    title: "Insights | KEV",
    description: "Every Premium board Kev keeps, NBA and football, in one place.",
    robots: "noindex",
  },
  /* The "see more" page from the landing spot — `/fixtures`, PUBLIC and
     INDEXABLE, same anonymous tier as `home` above and named distinctly
     from the `fixtures` key below, which is the Premium per-match market
     board at `/app/fixtures` and stays noindex. Two pages, two keys, on
     purpose — see routes/AllFixtures.tsx's own note. */
  allFixtures: {
    title: "All fixtures | KEV",
    description:
      "Every upcoming match Kev covers, ordered by whichever sport kicks off next — his read on each, and whether he's found value.",
  },
  fixtures: {
    title: "Football match markets | KEV",
    description:
      "Every market KEV prices for an upcoming match, with a fair price on each.",
    robots: "noindex",
  },
  footballFixturePage: {
    title: "Fixture breakdown | KEV",
    description:
      "Kev's full read on one match: the call against the market average, where the gaps sit, price movement, props for both clubs and team news.",
    robots: "noindex",
  },
  /* ITS OWN ENTRY, NOT FOOTBALL'S. The NFL page shows genuinely less — no odds
     feed is connected, so there is no market average to compare against, no
     price movement and no edge record, and there is no NFL prop board. Reusing
     football's description would have this page promise four things it does not
     have. What it has instead is the thing football's does not: a stated refusal
     to call most games, which is the honest headline. */
  nflFixturePage: {
    title: "NFL game breakdown | KEV",
    description:
      "Kev's read on one NFL game: his winning chances and fair odds, the games he won't call and why, team news and last season's form.",
    robots: "noindex",
  },
  season: {
    title: "Insight | KEV",
    description:
      "Projected wins and finishing odds for all 30 NBA teams, plus player form and " +
      "averages, from KEV's season model.",
    robots: "noindex",
  },
  player: {
    title: "Player deep dive | KEV",
    description:
      "Every regular-season game an NBA player played last season, and the spread behind his averages.",
    robots: "noindex",
  },
  /* Franco's room. noindex like every other signed-in page: a crawler reaching
     it gets the logged-out shell, and indexing that would advertise a page whose
     public copy is empty. */
  fantasy: {
    title: "Fantasy | KEV",
    description:
      "Franco's fantasy rooms, one per code: the shape each game plays, how it " +
      "scores, your saved squads, and what he does not know.",
    robots: "noindex",
  },
  props: {
    title: "Player props | KEV",
    description:
      "Every market KEV prices for an NBA club or player, with a fair price on each line.",
    robots: "noindex",
  },
  /* THE SPORT IS IN THE TITLE, for the reason boards.ts gives about its labels:
     there are two multi builders, and two tabs both reading "Multi builder" is a
     reader with no way to tell which is which. meta.test.ts enforces uniqueness,
     which is how this was caught rather than shipped. */
  footballMulti: {
    title: "Premier League multi builder | KEV",
    description:
      "Build a Premier League same-game multi: every market KEV prices for the match, "
      + "combined exactly, with one player prop and the lift it carries.",
    robots: "noindex",
  },
  multi: {
    title: "NBA multi builder | KEV",
    description:
      "Build an NBA same-game multi from the props that measurably move the result, " +
      "and see what the combination is worth.",
    robots: "noindex",
  },
  account: {
    title: "Your account | KEV",
    description: "Your KEV plan, credits and account settings.",
    robots: "noindex",
  },
  billingReturn: {
    /* Was "Confirming payment — KEV". The tab title is the first thing a
       customer sees on returning from checkout, sometimes before the page
       paints, and this page no longer describes the state of a payment
       anywhere (Ned, 2026-08-31). */
    title: "Thank you | KEV",
    description: "Your subscription with Kev.",
    robots: "noindex",
  },
  chat: {
    title: "Chat | KEV",
    description: "Ask a KEV model a question and see how it got there.",
    robots: "noindex",
  },
  terms: {
    title: "Terms of Service | KEV",
    description:
      "The terms you agree to when you use KEV, from ENCEE ANALYTICS PTY LTD trading as Run it Past Kev.",
  },
  privacy: {
    /* "…how to get it back or deleted" was the first draft, and content.test.ts
       refused it: `\bget it back\b` is a §1.3 chasing-losses phrase. Harmless
       about data, indistinguishable about money, and the rule cannot tell which
       one a search snippet is describing. Reworded rather than exempted. */
    title: "Privacy Policy | KEV",
    description:
      "What KEV collects, why, where it is stored, and how to see, correct or erase it.",
  },
  responsibleGambling: {
    title: "Responsible Gambling | KEV",
    description:
      "KEV is analytics, not advice. Gambling Help Online 1800 858 858 and Lifeline 13 11 14 are always here. 18+.",
  },
  unavailable: {
    title: "Australia only | KEV",
    description: "KEV is available in Australia only.",
  },
  notFound: {
    title: "Page not found | KEV",
    description: "That page doesn't exist. It might be a stale link.",
    robots: "noindex",
  },
} as const satisfies Record<string, PageMetaEntry>;

export type PageKey = keyof typeof PAGE_META;
